import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Table,
  Typography,
  Zoom,
  makeStyles,
  useMediaQuery,
  useTheme,
  CircularProgress
} from '@material-ui/core';
import React, { ComponentType, ReactElement, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowBack, Delete } from '@material-ui/icons';
import { TransitionProps } from '@material-ui/core/transitions';
import { SessionContext } from '../../contexts/session';
import { useMutation } from 'react-apollo';
import { CREATE_LOCATION_MEMBER, DELETE_LOCATION_MEMBER } from './mutations';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  pageHeading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  sectionHeading: {
    marginTop: '20px'
  },
  formContainer: {
    paddingTop: '10px'
  }
}));

interface ManageLocationUsersProps {
  location: any;
  onClose: () => void;
}

export const ManageLocationUsers = ({ location, onClose }: ManageLocationUsersProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const { account, locRefetch } = useContext(SessionContext);

  const [createNewLocationMember, { loading, error: graphqlError }] = useMutation(CREATE_LOCATION_MEMBER);
  const [deleteLocationMember] = useMutation(DELETE_LOCATION_MEMBER);

  const [isAddingUser, setIsAddingUser] = useState(false);
  const toggleAddingUser = () => setIsAddingUser((prev) => !prev);

  const [newUser, setNewUser] = useState<{
    email: string;
    firstName: string;
  }>({ email: '', firstName: '' });

  const [firstNameError, setFirstNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const hasError = value.trim() === ''; // Example validation for required field
    setFirstNameError(hasError);
    setNewUser({ ...newUser, firstName: value });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const hasError = !/^[^@]+@[^@]+\.[^@]+$/.test(value); // Example validation for email format
    setEmailError(hasError);
    setNewUser({ ...newUser, email: value });
  };

  const disableForm = loading || firstNameError || emailError || !newUser.email || !newUser.firstName;

  const handleNewLocationUserSave = async () => {
    if (disableForm) {
      return;
    }
    try {
      await createNewLocationMember({
        variables: {
          locationId: location._id,
          email: newUser.email,
          name: newUser.firstName
        }
      });

      await locRefetch();
      toggleAddingUser();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteLocationUser = async (email: string) => {
    try {
      await deleteLocationMember({
        variables: {
          locationId: location._id,
          email
        }
      });

      await locRefetch();
    } catch (e) {
      console.error(e);
    }
  };

  if (!location) {
    return null;
  }

  const locationUsers = account.users.filter((userAccess: any) => userAccess.locations.includes(location._id));

  return (
    <Dialog
      open={location !== null}
      TransitionComponent={Transition as ComponentType<TransitionProps & { children?: ReactElement<any, any> | undefined }>}
      keepMounted
      fullScreen={fullScreen}
      onClose={onClose}>
      <DialogContent>
        <>
          <Grid container>
            <Grid item xs={2}>
              <ArrowBack onClick={onClose} className={'cursor-pointer'} />
            </Grid>
            <Grid item xs={8} className={classes.pageHeading}>
              {t('LocationCard.ManageUsersTitle')}
            </Grid>
          </Grid>
          <Grid container className={classes.formContainer} spacing={2} style={{ marginBottom: 100 }}>
            <Grid item xs={12}>
              <Typography variant="h2" className={classes.sectionHeading}>
                {t('LocationCard.AssignedUsers')}
              </Typography>
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('ScreenEdit.Name')}</TableCell>
                        <TableCell align="left">{t('Account.Email')}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationUsers.map(
                        ({
                          user: {
                            _id,
                            firstName,
                            local: { email }
                          }
                        }: {
                          user: { _id: string; firstName: string; lastName: string; local: { email: string } };
                        }) => (
                          <TableRow key={email} onClick={() => {}}>
                            <TableCell>{firstName}</TableCell>
                            <TableCell align="left">{email}</TableCell>
                            <TableCell align="right" component="th" scope="row">
                              <Delete
                                onClick={() => handleDeleteLocationUser(email)}
                                style={{
                                  cursor: 'pointer'
                                }}
                                color="secondary"
                              />
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
            <Grid container item xs={12}>
              <Button onClick={toggleAddingUser} variant="contained" color="primary">
                {t('LocationCard.AddUser')}
              </Button>
              {isAddingUser && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h2">{t('LocationCard.NewUser')}</Typography>
                  </Grid>
                  <div>
                    <TextField
                      variant="outlined"
                      label={t('LocationCard.LocationName')}
                      value={newUser.firstName}
                      error={false}
                      onChange={handleFirstNameChange}
                      fullWidth
                    />
                    <TextField
                      className={classes.sectionHeading}
                      variant="outlined"
                      label={t('Signup.Email')}
                      value={newUser.email}
                      onChange={handleEmailChange}
                      fullWidth
                    />

                    <br />
                    {(emailError || graphqlError) && (
                      <Typography color="error" variant={'caption'} style={{ marginTop: 4 }}>
                        *{t(graphqlError?.graphQLErrors?.[0]?.message ?? 'Account.EmailError')}
                      </Typography>
                    )}
                  </div>
                  <Grid item xs={12}>
                    <Button
                      onClick={handleNewLocationUserSave}
                      className={emailError ? '' : classes.sectionHeading}
                      disabled={disableForm}
                      variant="contained"
                      color="primary">
                      {loading ? <CircularProgress size={24} color={'inherit'} /> : t('TapForm.Save')}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </>
      </DialogContent>
    </Dialog>
  );
};
